import React, { useState } from 'react'
import Swal from 'sweetalert2'
import useFetch from '../../hooks/useFetch'
import useForm from '../../hooks/useForm'
import { ucwords } from '../../utils/Ucwords'

const CreateScheduleModal = ({ isOpen, onClose, onSuccess, selectedDate }) => {    
    const API_URL = process.env.REACT_APP_API_URL;
    const [selectedService, setSelectedService] = useState('');

    const { data: fetchClients } = useFetch(`${API_URL}/consultation`)
    const { data: fetchAccountOfficer } = useFetch(`${API_URL}/schedules/ao`)
    
    const validate = async (values) => {
        const errors = {};
        if (!values.client_id) errors.client_id = "Please select client";
        if (!values.user_id) errors.user_id = "Please select account officer";
        if (!values.schedule_time) errors.schedule_time = "Please input schedule time";
        if (!values.sched_status) errors.sched_status = "Please select status";
        return errors;
    };

    const formattedDate = selectedDate ? new Date(selectedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    const { values, errors, isSubmitting, handleChange, handleSubmit, resetForm } = useForm(
        { 
            client_id: '',
            user_id: '',
            schedule_date: selectedDate || formattedDate,
            schedule_time: '',
            sched_status: '1', // scheduled
        },
        validate
    );

    // Handle client selection and update service
    const handleClientChange = (e) => {
        handleChange(e);
        const selectedClientId = e.target.value;
        const selectedClient = fetchClients.find(client => String(client.id) === String(selectedClientId));
        if (selectedClient && selectedClient.service) {
            setSelectedService(ucwords(selectedClient.service));
        } else {
            setSelectedService('');
            console.log('No service found for selected client');
        }
    };

    const onSubmit = async (values) => {
        console.log('Submitting values:', values); // Debugging line

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/schedules`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(values)
            });
    
            // Check content type before parsing
            const contentType = response.headers.get("content-type");
            if (!response.ok) {
                if (contentType && contentType.includes("application/json")) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to create schedule');
                } else {
                    const errorText = await response.text();
                    throw new Error(`Server error: ${errorText}`);
                }
            }
    
            const data = await response.json();
            resetForm();
    
            if (typeof onSuccess === 'function') {
                onSuccess(data);
            }
    
            onClose();
    
            Swal.fire({
                title: 'Success!',
                text: data.message,
                icon: 'success',
                showConfirmButton: false,
                timer: 5000
            });
        } catch (error) {
            console.error('Error creating schedule:', error.message);
            Swal.fire({
                title: 'Error!',
                text: error.message,
                icon: 'error',
                showConfirmButton: false,
                timer: 5000
            });
        }
    };
    
    return (
        <div 
            id="static-modal" 
            className={`fixed overflow-y-auto overflow-x-hidden inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 max-h-full ${isOpen ? 'flex' : 'hidden'}`} 
            data-modal-backdrop="static" 
            tabIndex="-1" 
            aria-hidden={!isOpen}
        >
            <div className="relative p-4 w-full max-w-2xl max-h-full">
                <div className="relative bg-white rounded-lg shadow">
                    <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
                        <h3 className="text-xl font-semibold text-gray-900" id="modal-title">
                            Create Schedule
                        </h3>
                        <button
                            type="button"
                            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                            onClick={onClose}
                        >
                            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                            </svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>
                    <div className="p-4 md:p-5 space-y-4">
                        <form onSubmit={(e) => handleSubmit(e, onSubmit)}>
                            <div className='flex flex-col gap-5 w-full'>
                                <div className='flex flex-col w-full gap-3'>
                                    <div className="w-full">
                                        <label htmlFor="client_id" className="block font-medium text-gray-700">Client Name</label>
                                        <select
                                            id="client_id"
                                            name="client_id"
                                            value={values.client_id}
                                            onChange={handleClientChange}
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5"
                                        >
                                            <option value="">Select Client Name</option>
                                            {fetchClients.map((client) => 
                                                <option key={client.id} value={client.id}>
                                                    {`${ucwords(client.firstname)} ${ucwords(client.lastname)}`}
                                                </option>
                                            )}
                                        </select>
                                        {errors.client_id && (
                                            <p className="text-red-600">{errors.client_id}</p>
                                        )}
                                    </div>

                                    <div className="w-full">
                                        <label htmlFor="inquiry" className="block font-medium text-gray-700">Inquiry</label>
                                        <input
                                            type="text"
                                            id="inquiry"
                                            name='inquiry'
                                            value={selectedService}
                                            className="bg-gray-200 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-none focus:border-none"
                                            readOnly
                                        />
                                    </div> 

                                    <div className="w-full mt-3">
                                        <label htmlFor="user_id" className="block font-medium text-gray-700">Account Officer</label>
                                        <select
                                            id="user_id"
                                            name="user_id"
                                            value={values.user_id}
                                            onChange={handleChange}
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5"
                                        >
                                            <option value="">Select Account Officer</option>
                                            {fetchAccountOfficer.map((employee) => (
                                                <option key={employee.id} value={employee.id}>
                                                    {ucwords(employee.firstname)} {ucwords(employee.lastname)}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.user_id && (
                                            <p className="text-red-600">{errors.user_id}</p>
                                        )}
                                    </div>

                                    <div className="flex gap-4 mt-3 my-4">
                                        <div className="w-1/2">
                                            <label htmlFor="schedule_date" className="block font-medium text-gray-700">Schedule Date</label>
                                            <input
                                                type="date"
                                                id="schedule_date"
                                                name="schedule_date"
                                                value={values.schedule_date}
                                                onChange={handleChange}
                                                className="bg-gray-200 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5"
                                                readOnly
                                            />
                                        </div>

                                        <div className="w-1/2">
                                            <label htmlFor="schedule_time" className="block font-medium text-gray-700">Schedule Time</label>
                                            <input
                                                type="time"
                                                id="schedule_time"
                                                name="schedule_time"
                                                value={values.schedule_time}
                                                onChange={handleChange}
                                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center p-2 border-t border-gray-200 rounded-b gap-5">
                                <button
                                    disabled={isSubmitting}
                                    type="submit"
                                    className="bg-green-500 hover:bg-green-600 text-white rounded-lg px-4 py-2"
                                >
                                    {isSubmitting ? 'Creating Schedule' : 'Create Schedule'}
                                </button>
                                <button
                                    type="button"
                                    className="text-gray-500 border border-gray-300 hover:bg-gray-100 rounded-lg px-4 py-2 mr-3"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateScheduleModal;